import React, { useCallback, useEffect, useState } from 'react';

import { EikaMessageEvent, GtpWalletInfoResult, useNativeEventListener } from '@eika-infrastruktur/mobilbank-lib';
import { parseCallback } from '@eika-infrastruktur/mobilbank-lib/src/useNativeEventListener';
import CenteredLoader from '@eika-kortbetjening/state-handling-modal/lib/loader/Loader';
import { ErrorMessage } from '@eika/attention-message';

import { useNavigate } from '@domain/app';
import { useGetGooglePayOPCSignedProvision } from '@domain/digital-wallet';
import { Card, DigitalWalletSignResponse } from '@generated-types';
import useText from '@hooks/use-text';

import PermissionHasBeenSigned from '../permission-has-been-signed/PermissionHasBeenSigned';
import { handleEndGTPWalletProcess, handlePushProvisionState, handleSigningState } from './functions';
import { PushProvisionWebAction, SigningWebAction } from './types';

import './PermissionSigning.scss';

interface PermissionSigningProps {
    card: Card;
    handlePushProvisionSuccess: () => void;
    signResponse: DigitalWalletSignResponse | undefined;
    googleWalletInfo: GtpWalletInfoResult;
}

const PermissionSigning = ({
    card,
    handlePushProvisionSuccess,
    signResponse,
    googleWalletInfo,
}: PermissionSigningProps) => {
    const text = useText();
    const navigate = useNavigate();

    const provisionMutation = useGetGooglePayOPCSignedProvision({ card, walletInfoResult: googleWalletInfo });

    const [signingError, setSigningError] = useState<string | null>(null);
    const [permissionHasBeenSigned, setPermissionHasBeenSigned] = useState<boolean>(false);

    const [pushProvisionError, setPushProvisionError] = useState<string | null>(null);

    useEffect(() => {
        if (signingError !== null || pushProvisionError !== null) {
            handleEndGTPWalletProcess(card, navigate);
        }
    }, [signingError, pushProvisionError, card, navigate]);

    useEffect(() => {
        if (!permissionHasBeenSigned || !signResponse) {
            return;
        }

        provisionMutation.mutate(signResponse);
    }, [permissionHasBeenSigned, provisionMutation, signResponse]);

    const handleSigningResponse = useCallback(
        (event: EikaMessageEvent<SigningWebAction>) => {
            const data = parseCallback(event.data);

            handleSigningState(data, () => setPermissionHasBeenSigned(true), setSigningError, text);
        },
        [text],
    );

    const handlePushProvisionResponse = useCallback(
        (event: EikaMessageEvent<PushProvisionWebAction>) => {
            const data = parseCallback(event.data);

            handlePushProvisionState(data, setPushProvisionError, handlePushProvisionSuccess, text);
        },
        [handlePushProvisionSuccess, text],
    );

    useNativeEventListener(handleSigningResponse);
    useNativeEventListener(handlePushProvisionResponse);

    if (signResponse === undefined) {
        return <CenteredLoader text={text.loadingGTPWalletSigning} />;
    }

    if (signingError !== null) {
        return <ErrorMessage text={signingError} moreText={text.googleWalletRedirectToGoogleWalletInfoPage} />;
    }

    if (pushProvisionError !== null) {
        return <ErrorMessage text={pushProvisionError} moreText={text.googleWalletRedirectToGoogleWalletInfoPage} />;
    }

    if (permissionHasBeenSigned) {
        return <PermissionHasBeenSigned card={card} />;
    }

    return null;
};

export default PermissionSigning;
